import { ForbiddenError } from '../../errors/ForbiddenError';
import { NetworkError } from '../../errors/NetworkError';
import { Fetcher } from '../../Fetcher';

const PROMO_ENDPOINT = '/api/v2/fetchPromotionsInfo';

import type {
  IJwtResponse,
  IPromotionsData,
  IPromotionResponse,
  IPromoPointsResponse,
  IPromoPointsData,
} from './types';

export interface FetchPromotionParams {
  envGs: string;
  envPromo: string;
  jwtToken: string;
  lang: string;
  userId: string;
  JSESSIONID: string;
  operatorGameId: string;
  force?: boolean;
}

export interface FetchPromotionTranslationParams {
  url: string;
  buildHashParam: { params?: { buildHash: string } };
}

export interface PostPromoScreenNameParams {
  envPromo: string;
  jwtToken: string;
}

export interface FetchPromoPointsParams {
  envPromo: string;
  jwtToken: string;
}

export const fetchPromotions = ({
  envPromo,
  jwtToken,
  operatorGameId,
  lang,
  force = false,
}: FetchPromotionParams): Promise<IPromotionsData> => {
  return Fetcher.fetch<IPromotionResponse>(envPromo + PROMO_ENDPOINT, {
    force: force,
    headers: {
      Authorization: `Bearer ${jwtToken}`,
    },
    params: {
      detailed: 'true',
      lang,
    },
  }).then((response: IPromotionResponse) => {
    if (response.obType == 'ERROR') {
      if (response.data.indexOf('Unsupported') === 0) {
        throw new ForbiddenError(response.data);
      } else {
        throw new NetworkError('fetchPromotions', response.data, PROMO_ENDPOINT);
      }
    } else if (response.obType == 'PROMOTIONS') {
      const regEx = new RegExp(`\\b${operatorGameId}\\b`);
      return {
        pds: response.data.pds.filter((promo) => promo.opt !== 'NA' && regEx.exec(promo.gms)),
        tms: response.data.tms.filter((promo) => promo.opt !== 'NA' && regEx.exec(promo.gms)),
        cbs: response.data.cbs.filter((promo) => promo.opt !== 'NA' && regEx.exec(promo.gms)),
      };
    }
    throw new Error('Unknown `obType` field');
  });
};

export const fetchPromoTranslations = ({
  url,
  buildHashParam,
}: FetchPromotionTranslationParams): Promise<Record<string, string>> => {
  return Fetcher.fetch<{ game: Record<string, string> }>(url, buildHashParam).then((response) => response.game);
};

export const postPromoScreenName = ({ envPromo, jwtToken }: PostPromoScreenNameParams): Promise<void> => {
  return Fetcher.fetch<void>(envPromo + '/api/updateScreenName', {
    headers: {
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });
};

export const getJwtTokenForPromo = ({ envGs, userId, JSESSIONID }: FetchPromotionParams): Promise<string> => {
  return Fetcher.fetch<IJwtResponse>(envGs + '/api/ui/updateJwtToken', {
    params: {
      userId,
      JSESSIONID,
    },
  }).then((response) => response.jwtToken);
};

export const fetchPromoPoints = ({ envPromo, jwtToken }: FetchPromoPointsParams): Promise<IPromoPointsData> => {
  return Fetcher.fetch<IPromoPointsResponse>(envPromo + '/api/fetchinstantpoints', {
    force: true,
    headers: {
      Authorization: `Bearer ${jwtToken}`,
    },
  }).then((response) => {
    const parsedResponse: IPromoPointsData = {};
    Object.entries(response).forEach(([key, item]) => {
      parsedResponse[key] = {
        type: item.type,
        lastGameId: item.lastGameId,
        isRoundLimitExceeded: item.irle,
        ts: item.ts,
        sb: item.sb,
        winStreak: item.ws,
        lastWonScore: item.lws,
      };
    });
    return parsedResponse;
  });
};
